import { createAppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { mainnet, polygon, arbitrum, avalanche, base, optimism, bsc } from '@reown/appkit/networks';
import { createConfig, http } from 'wagmi';

export const REOWN_PROJECT_ID = '687ae3b592e6cb1c146144cc2847abdb';

// Supported networks for WalletConnect
export const WALLET_NETWORKS = [mainnet, polygon, arbitrum, avalanche, base, optimism, bsc];

// Wagmi adapter for Reown AppKit
export const wagmiAdapter = new WagmiAdapter({
  networks: WALLET_NETWORKS,
  projectId: REOWN_PROJECT_ID,
});

// Init AppKit (must be called once at app root level)
let appKitInitialised = false;
export function initAppKit() {
  if (appKitInitialised) return;
  appKitInitialised = true;

  createAppKit({
    adapters: [wagmiAdapter],
    networks: WALLET_NETWORKS as any,
    projectId: REOWN_PROJECT_ID,
    metadata: {
      name: 'Catat Crypto',
      description: 'Track your crypto trades across CEX and on-chain wallets',
      url: 'https://catatcrypto.com',
      icons: ['https://catatcrypto.com/favicon.ico'],
    },
    features: {
      analytics: false,
      email: false,
      socials: [],
    },
    themeMode: 'dark',
    themeVariables: {
      '--w3m-accent': '#E26A59',
      '--w3m-border-radius-master': '8px',
    },
  });
}

// Supported networks config for the manual add form (mirrors backend supportedNetworks)
export interface WalletNetwork {
  value: string;
  label: string;
  symbol: string;
  color: string;
  icon: string;
  addressPrefix?: string; // for basic client-side validation
  explorer: string;
}

export const SUPPORTED_WALLET_NETWORKS: WalletNetwork[] = [
  {
    value: 'ethereum',
    label: 'Ethereum',
    symbol: 'ETH',
    color: '#627EEA',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png',
    addressPrefix: '0x',
    explorer: 'https://etherscan.io',
  },
  {
    value: 'solana',
    label: 'Solana',
    symbol: 'SOL',
    color: '#9945FF',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5426.png',
    explorer: 'https://solscan.io',
  },
  {
    value: 'bsc',
    label: 'BNB Chain',
    symbol: 'BNB',
    color: '#F3BA2F',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png',
    addressPrefix: '0x',
    explorer: 'https://bscscan.com',
  },
  {
    value: 'polygon',
    label: 'Polygon',
    symbol: 'MATIC',
    color: '#8247E5',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/3890.png',
    addressPrefix: '0x',
    explorer: 'https://polygonscan.com',
  },
  {
    value: 'arbitrum',
    label: 'Arbitrum',
    symbol: 'ETH',
    color: '#28A0F0',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/11841.png',
    addressPrefix: '0x',
    explorer: 'https://arbiscan.io',
  },
  {
    value: 'avalanche',
    label: 'Avalanche',
    symbol: 'AVAX',
    color: '#E84142',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/5805.png',
    addressPrefix: '0x',
    explorer: 'https://snowtrace.io',
  },
  {
    value: 'base',
    label: 'Base',
    symbol: 'ETH',
    color: '#0052FF',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/27716.png',
    addressPrefix: '0x',
    explorer: 'https://basescan.org',
  },
  {
    value: 'optimism',
    label: 'Optimism',
    symbol: 'ETH',
    color: '#FF0420',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/11840.png',
    addressPrefix: '0x',
    explorer: 'https://optimistic.etherscan.io',
  },
  {
    value: 'bitcoin',
    label: 'Bitcoin',
    symbol: 'BTC',
    color: '#F7931A',
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/1.png',
    addressPrefix: '1',
    explorer: 'https://mempool.space',
  },
];

export function validateWalletAddress(network: string, address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed) return 'Address is required';

  const net = SUPPORTED_WALLET_NETWORKS.find(n => n.value === network);
  if (!net) return 'Unsupported network';

  // Basic EVM check
  if (net.addressPrefix === '0x') {
    if (!/^0x[0-9a-fA-F]{40}$/.test(trimmed)) {
      return 'Invalid EVM address (must be 0x followed by 40 hex characters)';
    }
  }

  // Solana: base58, typically 32-44 chars
  if (network === 'solana') {
    if (trimmed.length < 32 || trimmed.length > 44) {
      return 'Invalid Solana address';
    }
  }

  // Bitcoin: loose check
  if (network === 'bitcoin') {
    if (trimmed.length < 25 || trimmed.length > 62) {
      return 'Invalid Bitcoin address';
    }
  }

  return null;
}
